import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import Slide from '@mui/material/Slide';
import appConfig from '../config/app';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RMAFormModal = ({ open, onClose, onMessage, onRmaCreated, navigate, userType, auth }) => {
  const isServiceman = userType === 'serviceman';
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    contact_person: '',
    phone: '',
    product_code: '',
    product_name: '',
    serial_number: '',
    purchase_date: '',
    delivery_note_number: '',
    description: '',
    email: isServiceman ? '' : auth.user?.profile?.email || '',
  });

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conditionsAccepted, setConditionsAccepted] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [descriptionError, setDescriptionError] = useState('');
  const [productError, setProductError] = useState('');
  const [serialNumberError, setSerialNumberError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [purchaseDateError, setPurchaseDateError] = useState('');

  const validateDescription = (description) => {
    if (!description.trim()) {
      return 'Description is required';
    }


    if (description.trim().length < 30) {
      return 'Description must be at least 30 characters long';
    }
    if (description.length > 500) {
      return 'Description cannot exceed 500 characters';
    }
    return '';
  };

  const validateProductFields = (product_code, product_name) => {
    if (!product_code.trim() && !product_name.trim()) {
      return 'Either Product Code or Product Name is required';
    }
    return '';
  };

  const validatePhone = (phone) => {

    if (!phone.trim()) return '';

    const phonePattern = /^\+?[\d\s\-\(\)]{7,20}$/;
    if (!phonePattern.test(phone)) {
      return 'Please enter a valid phone number (e.g., +420 123 456 789)';
    }
    return '';
  };

  const getTodayLocalString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validatePurchaseDate = (date) => {
    if (!date) return '';
    const todayString = getTodayLocalString();

    if (date > todayString) {
      return 'Purchase date cannot be in the future';
    }
    return '';
  };

  const validateSerialNumber = (serialNumber) => {
    const serialNumberPattern = /^\d+(\(\d+\))?$/;
    if (!serialNumber.trim()) {
      return 'Serial Number is required';
    }
    if (!serialNumberPattern.test(serialNumber)) {
      return 'Serial Number must be numeric with optional parentheses containing numbers (e.g., 015846 or 015846(06))';
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!emailPattern.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'description') {
      setDescriptionError(validateDescription(value));
    }
    if (name === 'product_code' || name === 'product_name') {
      setProductError(
        validateProductFields(
          name === 'product_code' ? value : formData.product_code,
          name === 'product_name' ? value : formData.product_name
        )
      );
    }
    if (name === 'serial_number') {
      setSerialNumberError(validateSerialNumber(value));
    }
    if (name === 'email') {
      setEmailError(validateEmail(value));
    }
    if (name === 'phone') {
      setPhoneError(validatePhone(value));
    }
    if (name === 'purchase_date') {
      setPurchaseDateError(validatePurchaseDate(value));
    }

  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const descriptionValidationError = validateDescription(formData.description);
    if (descriptionValidationError) {
      setDescriptionError(descriptionValidationError);
      onMessage(descriptionValidationError, 'error');
      return;
    }
    const phoneValidationError = validatePhone(formData.phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      onMessage(phoneValidationError, 'error');
      return;
    }

    const dateValidationError = validatePurchaseDate(formData.purchase_date);
    if (dateValidationError) {
      setPurchaseDateError(dateValidationError);
      onMessage(dateValidationError, 'error');
      return;
    }
    const productValidationError = validateProductFields(formData.product_code, formData.product_name);
    if (productValidationError) {
      setProductError(productValidationError);
      onMessage(productValidationError, 'error');
      return;
    }

    const serialNumberValidationError = validateSerialNumber(formData.serial_number);
    if (serialNumberValidationError) {
      setSerialNumberError(serialNumberValidationError);
      onMessage(serialNumberValidationError, 'error');
      return;
    }

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      onMessage(emailValidationError, 'error');
      return;
    }

    if (!conditionsAccepted || !gdprAccepted) {
      onMessage('Please accept both conditions and GDPR policy to proceed.', 'error');
      return;
    }

    if (!auth.user?.access_token || !auth.user?.profile?.sub) {
      onMessage('You must be logged in to create an RMA.', 'error');
      return;
    }

    setLoading(true);

    try {
      const token = auth.user.access_token;
      const formDataToSend = new FormData();

      // Append form fields
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      // Append cognito_user_id
      formDataToSend.append('cognito_user_id', auth.user.profile.sub);

      // Append attachments with keys starting with attachment_
      attachments.forEach((file, index) => {
        formDataToSend.append(`attachment_${index}`, file);
      });

      const response = await fetch(`${appConfig.api.baseUrl}/rma/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const errorData = await response.json();
          throw new Error(errorData.detail || errorData.error || 'Failed to create RMA');
        } else {
          const errorText = await response.text();
          throw new Error(errorText || 'An unknown error occurred on the server.');
        }
      }

      const result = await response.json();
      onMessage(
        `RMA created successfully! RMA Number: ${result.rma_number}. Redirecting to details...`,
        'success'
      );

      // Reset form
      setFormData({
        company_name: '',
        address: '',
        city: '',
        postal_code: '',
        country: '',
        contact_person: '',
        phone: '',
        product_code: '',
        product_name: '',
        serial_number: '',
        purchase_date: '',
        delivery_note_number: '',
        description: '',
        email: isServiceman ? '' : auth.user?.profile?.email || '',
      });
      setAttachments([]);
      setConditionsAccepted(false);
      setGdprAccepted(false);
      setDescriptionError('');
      setProductError('');
      setSerialNumberError('');
      setEmailError('');

      setPhoneError('');
      setPurchaseDateError('');

      // Trigger the onRmaCreated callback
      if (onRmaCreated) {
        onRmaCreated();
      }

      onClose();
      navigate(`/rma/${result.id}`);
    } catch (error) {
      console.error('Error creating RMA:', error);
      onMessage(`Error creating RMA: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setFormData({
          company_name: '',
          address: '',
          city: '',
          postal_code: '',
          country: '',
          contact_person: '',
          phone: '',
          product_code: '',
          product_name: '',
          serial_number: '',
          purchase_date: '',
          delivery_note_number: '',
          description: '',
          email: isServiceman ? '' : auth.user?.profile?.email || '',
        });
        setAttachments([]);
        setConditionsAccepted(false);
        setGdprAccepted(false);
        setDescriptionError('');
        setProductError('');
        setSerialNumberError('');
        setEmailError('');
        setPhoneError('');
        setPurchaseDateError('');
        onClose();
      }}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold">
                RMA FORM
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (Return Material Authorization)
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => {
              setFormData({
                company_name: '',
                address: '',
                city: '',
                postal_code: '',
                country: '',
                contact_person: '',
                phone: '',
                product_code: '',
                product_name: '',
                serial_number: '',
                purchase_date: '',
                delivery_note_number: '',
                description: '',
                email: isServiceman ? '' : auth.user?.profile?.email || '',
              });
              setAttachments([]);
              setConditionsAccepted(false);
              setGdprAccepted(false);
              setDescriptionError('');
              setProductError('');
              setSerialNumberError('');
              setEmailError('');
              setPhoneError('');
              setPurchaseDateError('');
              onClose();
            }}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Conditions for the return:
          </Typography>
          <Typography variant="body2" paragraph>
            Only products purchased from UTILCELL, s.r.o. can be requested for return. Return may be required for upgrade, service calibration, repair or replacement due manufacturing defect in warranty period.
          </Typography>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Prior returning material, it is necessary to do the following:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mb: 1 }}>
            <li>Fill the RMA form and submit it through this system.</li>
            <li>You will receive an RMA number via email with further instructions.</li>
            <li>Include the RMA number on the shipment in a visible place.</li>
            <li>Send the package to our postal address.</li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Important note: If the package with products for return is sent without RMA number, it will be returned.
            </Typography>
          </Alert>
        </Alert>

        <Box component="form" onSubmit={handleSubmit}>
          <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              Company Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>

                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!emailError}
                  helperText={emailError || (isServiceman ? "Enter the customer's email address" : 'Your email address')}
                  InputProps={{
                    readOnly: !isServiceman,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  error={!!phoneError}
                  helperText={phoneError}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              Product Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Product Code"
                  name="product_code"
                  value={formData.product_code}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  error={!!productError}
                  helperText={productError || 'Either Product Code or Product Name is required'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  error={!!productError}
                  helperText={productError || 'Either Product Code or Product Name is required'}
                />
              </Grid>


              <Grid item xs={12} md={4} sx={{ width: '100%', minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Serial Number"
                  name="serial_number"
                  value={formData.serial_number}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                  error={!!serialNumberError}

                  helperText={serialNumberError || 'Numeric value, optionally with parentheses (e.g., 015846(06))'}
                  FormHelperTextProps={{
                    sx: {
                      minHeight: '40px',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ width: '100%', minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Purchase Date"
                  name="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: getTodayLocalString() }}
                  error={!!purchaseDateError}
                  helperText={purchaseDateError || ' '}
                  FormHelperTextProps={{ sx: { minHeight: '40px' } }}
                />
              </Grid>

              <Grid item xs={12} md={4} sx={{ width: '100%', minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Delivery Note Number"
                  name="delivery_note_number"
                  value={formData.delivery_note_number}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  helperText=" "
                  FormHelperTextProps={{ sx: { minHeight: '40px' } }}
                />
              </Grid>



              <Grid item xs={12} sx={{ width: '100%', minWidth: '100%' }}>
                <TextField
                  fullWidth
                  label="Description of the reason for return"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Describe the issue or reason for return..."
                  error={!!descriptionError}
                  helperText={descriptionError || 'Note: Avoid vague description as "not working". Be descriptive as much as possible (min 30 characters, max 500 characters).'}
                  FormHelperTextProps={{
                    sx: {
                      minHeight: '45px',
                      marginTop: '4px',
                      lineHeight: '1.2'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              Attachments (Optional)
            </Typography>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Select Files
            </Button>

            {attachments.length > 0 && (
              <List dense>
                {attachments.map((file, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => removeAttachment(index)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              Agreements
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={conditionsAccepted}
                  onChange={(e) => setConditionsAccepted(e.target.checked)}
                  required
                  color="primary"
                />
              }
              label={

                <Typography variant="body2" component="span">
                  I accept the <strong>conditions for return</strong> as stated above. I understand that products sent without RMA number will be returned, and costs may be recovered for unfounded complaints.
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={gdprAccepted}
                  onChange={(e) => setGdprAccepted(e.target.checked)}
                  required
                  color="primary"
                />
              }
              label={

                <Typography variant="body2" component="span">
                  I consent to the processing of my personal data for the purpose of handling this RMA request in accordance with <strong>GDPR</strong> regulations.
                </Typography>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Paper>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => {
                setFormData({
                  company_name: '',
                  address: '',
                  city: '',
                  postal_code: '',
                  country: '',
                  contact_person: '',
                  phone: '',
                  product_code: '',
                  product_name: '',
                  serial_number: '',
                  purchase_date: '',
                  delivery_note_number: '',
                  description: '',
                  email: isServiceman ? '' : auth.user?.profile?.email || '',
                });
                setAttachments([]);
                setConditionsAccepted(false);
                setGdprAccepted(false);
                setDescriptionError('');
                setProductError('');
                setSerialNumberError('');
                setEmailError('');
                setPhoneError('');
                setPurchaseDateError('');
                onClose();
              }}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !conditionsAccepted || !gdprAccepted || !!descriptionError || !!productError || !!serialNumberError || !!emailError || !!phoneError || !!purchaseDateError}
              sx={{
                textTransform: 'none',
                px: 3,
                minWidth: 140,
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating...
                </>
              ) : (
                'Submit RMA Request'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RMAFormModal;